import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { fetchClusterById, updateCluster } from '../store/slices/clusterSlice';
import { fetchTenants } from '../store/slices/tenantSlice';
import { addNotification } from '../store/slices/uiSlice';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';
import { FiArrowLeft, FiEdit2, FiSave, FiX, FiTrash2, FiPlus, FiUser } from 'react-icons/fi';
import Breadcrumbs from '../components/Breadcrumbs';

// Validation schema
const ClusterSchema = Yup.object().shape({
    clusterId: Yup.string()
        .required('Cluster ID is required')
        .matches(/^[a-zA-Z0-9-_]+$/, 'Only alphanumeric characters, hyphens, and underscores are allowed'),
    Servers: Yup.array().of(
        Yup.object().shape({
            Host: Yup.string().required('Host is required'),
            Port: Yup.number().required('Port is required').min(1).max(65535),
        })
    ).min(1, 'At least one server is required'),
    UserName: Yup.string().required('Username is required'),
    SecretStoreName: Yup.string().required('Secret store name is required'),
    SecretKey: Yup.string().required('Secret key is required'),
    UseTls: Yup.boolean(),
    Scheme: Yup.string().required('Scheme is required'),
    AuthenticationMechanism: Yup.string().required('Authentication mechanism is required'),
    AuthenticationSource: Yup.string().required('Authentication source is required'),
});

const ClusterDetailsPage: React.FC = () => {
    const { id, clusterName } = useParams<{ id: string, clusterName?: string }>();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const { selectedCluster, loadingCluster, error, clusters } = useSelector((state: RootState) => ({
        ...state.clusters,
        clusters: state.clusters, // Assuming clustersList contains all clusters
    }));
    const { tenants } = useSelector((state: RootState) => state.tenants);
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        if (id) {
            dispatch(fetchClusterById(id));
            dispatch(fetchTenants());
        }
    }, [dispatch, id]);

    const handleUpdateCluster = async (values: any) => {
        if (!id) return;

        try {
            await dispatch(updateCluster({ id, updateData: values })).unwrap();

            dispatch(
                addNotification({
                    type: 'success',
                    message: `Cluster "${values.clusterId}" was successfully updated.`,
                })
            );

            setIsEditing(false);
        } catch (error: any) {
            dispatch(
                addNotification({
                    type: 'error',
                    title: 'Update Failed',
                    message: error.message || 'Failed to update cluster.',
                })
            );
        }
    };

    // Filter tenants using this cluster
    const tenantsUsingCluster = selectedCluster
        ? tenants.filter(tenant => tenant.cluster === selectedCluster.clusterId)
        : [];

    if (loadingCluster && !selectedCluster) {
        return (
            <div className="flex justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (!selectedCluster && !loadingCluster) {
        return (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                <div className="text-center">
                    <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-white">Cluster Not Found</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        The cluster you're looking for does not exist or you do not have permission to view it.
                    </p>
                    <div className="mt-6">
                        <button
                            onClick={() => navigate('/clusters')}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            <FiArrowLeft className="mr-2" />
                            Back to Db Clusters
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!selectedCluster) return null;

    return (
        <div className="container mx-auto px-4 py-6">
            <Breadcrumbs />
            <div className="space-y-6">
                {/* Header with back button and actions */}
                <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
                    <div className="flex items-center">
                        <button
                            onClick={() => navigate('/clusters')}
                            className="mr-4 inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            <FiArrowLeft className="mr-2" />
                            Back to Db Clusters
                        </button>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                            {selectedCluster.clusterId}
                        </h1>
                    </div>

                    <div className="flex items-center space-x-3">
                        {!isEditing ? (
                            <button
                                onClick={() => setIsEditing(true)}
                                className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                                <FiEdit2 className="mr-2" />
                                Edit Cluster
                            </button>
                        ) : (
                            <button
                                onClick={() => setIsEditing(false)}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                                <FiX className="mr-2" />
                                Cancel Editing
                            </button>
                        )}
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-md p-4 text-sm text-red-600 dark:text-red-300">
                        {error}
                    </div>
                )}

                <div className="bg-white dark:bg-gray-800 shadow overflow-hidden rounded-lg">
                    {isEditing ? (
                        <Formik
                            initialValues={selectedCluster}
                            validationSchema={ClusterSchema}
                            onSubmit={handleUpdateCluster}
                        >
                            {({ values, isValid, dirty }) => (
                                <Form className="p-6 space-y-6">
                                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                        <div className="sm:col-span-3">
                                            <label htmlFor="clusterId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Cluster ID
                                            </label>
                                            <div className="mt-1">
                                                <Field
                                                    name="clusterId"
                                                    type="text"
                                                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                />
                                            </div>
                                            <ErrorMessage name="clusterId" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                        </div>

                                        <div className="sm:col-span-3">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Display Name</label>
                                            <div className="mt-1">
                                                <Field name="name" type="text"
                                                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md" />
                                            </div>
                                        </div>

                                        <div className="sm:col-span-3">
                                            <label htmlFor="UserName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Username</label>
                                            <div className="mt-1">
                                                <Field name="UserName" type="text"
                                                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md" />
                                            </div>
                                            <ErrorMessage name="UserName" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                        </div>

                                        <div className="sm:col-span-6">
                                            <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
                                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                                    Servers
                                                </h3>
                                            </div>

                                            <FieldArray name="Servers">
                                                {({ remove, push }) => (
                                                    <div className="space-y-4 mt-4">
                                                        {values.Servers.map((server, index) => (
                                                            <div key={index} className="flex items-center space-x-2">
                                                                <div className="flex-grow grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                                    <div>
                                                                        <label htmlFor={`Servers.${index}.Host`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                            Host
                                                                        </label>
                                                                        <Field
                                                                            name={`Servers.${index}.Host`}
                                                                            type="text"
                                                                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                                        />
                                                                        <ErrorMessage name={`Servers.${index}.Host`} component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                                                    </div>
                                                                    <div>
                                                                        <label htmlFor={`Servers.${index}.Port`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                            Port
                                                                        </label>
                                                                        <Field
                                                                            name={`Servers.${index}.Port`}
                                                                            type="number"
                                                                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                                        />
                                                                        <ErrorMessage name={`Servers.${index}.Port`} component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                                                    </div>
                                                                </div>
                                                                {values.Servers.length > 1 && (
                                                                    <button
                                                                        type="button"
                                                                        className="flex-shrink-0 mt-5 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                                                        onClick={() => remove(index)}
                                                                    >
                                                                        <FiTrash2 className="h-5 w-5" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ))}
                                                        <button
                                                            type="button"
                                                            onClick={() => push({ Host: '', Port: 27017 })}
                                                            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                                        >
                                                            <FiPlus className="mr-2" />
                                                            Add Server
                                                        </button>
                                                    </div>
                                                )}
                                            </FieldArray>
                                        </div>

                                        <div className="sm:col-span-3">
                                            <label htmlFor="Scheme" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Scheme
                                            </label>
                                            <div className="mt-1">
                                                <Field
                                                    as="select"
                                                    name="Scheme"
                                                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                >
                                                    <option value="MongoDB">MongoDB</option>
                                                    <option value="MongoDBPlusSrv">MongoDBPlusSrv</option>
                                                </Field>
                                            </div>
                                            <ErrorMessage name="Scheme" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                        </div>

                                        <div className="sm:col-span-3">
                                            <label htmlFor="UseTls" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                TLS Settings
                                            </label>
                                            <div className="mt-3">
                                                <div className="flex items-center">
                                                    <Field
                                                        type="checkbox"
                                                        name="UseTls"
                                                        id="UseTls"
                                                        className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded"
                                                    />
                                                    <label htmlFor="UseTls" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                                        Use TLS
                                                    </label>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="sm:col-span-3">
                                            <label htmlFor="AuthenticationMechanism" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Authentication Mechanism
                                            </label>
                                            <div className="mt-1">
                                                <Field
                                                    as="select"
                                                    name="AuthenticationMechanism"
                                                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                >
                                                    <option value="SCRAM-SHA-1">SCRAM-SHA-1</option>
                                                    <option value="SCRAM-SHA-256">SCRAM-SHA-256</option>
                                                    <option value="MONGODB-CR">MONGODB-CR</option>
                                                </Field>
                                            </div>
                                            <ErrorMessage name="AuthenticationMechanism" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                        </div>

                                        <div className="sm:col-span-3">
                                            <label htmlFor="AuthenticationSource" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Authentication Source
                                            </label>
                                            <div className="mt-1">
                                                <Field
                                                    name="AuthenticationSource"
                                                    type="text"
                                                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                />
                                            </div>
                                            <ErrorMessage name="AuthenticationSource" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                        </div>

                                        <div className="sm:col-span-6">
                                            <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
                                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                                    Secret Management
                                                </h3>
                                            </div>

                                            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 mt-4">
                                                <div className="sm:col-span-3">
                                                    <label htmlFor="SecretStoreName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        Secret Store Name
                                                    </label>
                                                    <div className="mt-1">
                                                        <Field
                                                            name="SecretStoreName"
                                                            type="text"
                                                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                        />
                                                    </div>
                                                    <ErrorMessage name="SecretStoreName" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                                </div>

                                                <div className="sm:col-span-3">
                                                    <label htmlFor="SecretKey" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password Secret Key</label>
                                                    <div className="mt-1">
                                                        <Field name="SecretKey" type="text"
                                                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md" />
                                                    </div>
                                                    <ErrorMessage name="SecretKey" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                                </div>

                                                <div className="sm:col-span-3">
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        Username Secret Key <span className="text-gray-400 text-xs">(if username is in vault)</span>
                                                    </label>
                                                    <div className="mt-1">
                                                        <Field name="UserNameKey" type="text" placeholder="Database-Cluster-UserName"
                                                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md" />
                                                    </div>
                                                </div>

                                                <div className="sm:col-span-3">
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        Replica Set Name <span className="text-gray-400 text-xs">(e.g. rs0 — blank for Atlas)</span>
                                                    </label>
                                                    <div className="mt-1">
                                                        <Field name="ReplicaSetName" type="text" placeholder="rs0"
                                                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md" />
                                                    </div>
                                                </div>

                                                <div className="sm:col-span-3 flex items-center gap-3 pt-6">
                                                    <Field type="checkbox" name="AllowInsecureTls" id="AllowInsecureTls"
                                                        className="h-4 w-4 text-primary-600 border-gray-300 rounded" />
                                                    <label htmlFor="AllowInsecureTls" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        Allow Insecure TLS <span className="text-gray-400 text-xs">(local Docker only)</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex justify-end space-x-3 pt-5 border-t border-gray-200 dark:border-gray-700">
                                        <button
                                            type="button"
                                            onClick={() => setIsEditing(false)}
                                            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                        >
                                            <FiX className="mr-2" />
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={!(isValid && dirty) || loadingCluster}
                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-400 disabled:cursor-not-allowed"
                                        >
                                            {loadingCluster ? (
                                                <>
                                                    <svg
                                                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <circle
                                                            className="opacity-25"
                                                            cx="12"
                                                            cy="12"
                                                            r="10"
                                                            stroke="currentColor"
                                                            strokeWidth="4"
                                                        ></circle>
                                                        <path
                                                            className="opacity-75"
                                                            fill="currentColor"
                                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                        ></path>
                                                    </svg>
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <FiSave className="mr-2" />
                                                    Save Changes
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    ) : (
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Cluster Details</h3>
                                    <dl className="mt-4 space-y-4">
                                        <div>
                                            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Cluster ID</dt>
                                            <dd className="mt-1 text-sm text-gray-900 dark:text-white">{selectedCluster.clusterId}</dd>
                                        </div>
                                        {(selectedCluster as any).name && (
                                            <div>
                                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</dt>
                                                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{(selectedCluster as any).name}</dd>
                                            </div>
                                        )}
                                        <div>
                                            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Username</dt>
                                            <dd className="mt-1 text-sm text-gray-900 dark:text-white">{selectedCluster.UserName}</dd>
                                        </div>
                                        <div>
                                            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Scheme</dt>
                                            <dd className="mt-1 text-sm text-gray-900 dark:text-white">{selectedCluster.Scheme}</dd>
                                        </div>
                                        <div>
                                            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Authentication</dt>
                                            <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                                                {selectedCluster.AuthenticationMechanism} ({selectedCluster.AuthenticationSource})
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">TLS Enabled</dt>
                                            <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                                                {selectedCluster.UseTls ? 'Yes' : 'No'}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Password Secret</dt>
                                            <dd className="mt-1 text-sm font-mono text-gray-900 dark:text-white">
                                                {selectedCluster.SecretStoreName} / {selectedCluster.SecretKey}
                                            </dd>
                                        </div>
                                        {(selectedCluster as any).UserNameKey && (
                                            <div>
                                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Username Secret Key</dt>
                                                <dd className="mt-1 text-sm font-mono text-gray-900 dark:text-white">{(selectedCluster as any).UserNameKey}</dd>
                                            </div>
                                        )}
                                        {(selectedCluster as any).ReplicaSetName && (
                                            <div>
                                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Replica Set</dt>
                                                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{(selectedCluster as any).ReplicaSetName}</dd>
                                            </div>
                                        )}
                                        {(selectedCluster as any).AllowInsecureTls && (
                                            <div>
                                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Insecure TLS</dt>
                                                <dd className="mt-1 text-sm text-amber-600 dark:text-amber-400">Enabled (cert validation skipped)</dd>
                                            </div>
                                        )}
                                        <div>
                                            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Created At</dt>
                                            <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                                                {new Date(selectedCluster.createdAt).toLocaleString()}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Updated At</dt>
                                            <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                                                {new Date(selectedCluster.updatedAt).toLocaleString()}
                                            </dd>
                                        </div>
                                    </dl>
                                </div>

                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Servers</h3>
                                    <ul className="mt-4 space-y-4">
                                        {selectedCluster.Servers.map((server, idx) => (
                                            <li key={idx} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {server.Host}:{server.Port}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>

                                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mt-6">Tenants Using This Cluster</h3>
                                    <div className="mt-4">
                                        {tenantsUsingCluster.length > 0 ? (
                                            <ul className="space-y-2">
                                                {tenantsUsingCluster.map(tenant => (
                                                    <li key={tenant._id} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                                                        <div className="flex items-center">
                                                            <FiUser className="mr-2 text-gray-400" />
                                                            <div>
                                                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                                    {tenant.tenantId}
                                                                </div>
                                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                    {tenant.baseUrl}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md text-center">
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    No tenants are currently using this cluster.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClusterDetailsPage;